import React, { useMemo, useState } from 'react';
import { HomeOutlined, ProjectOutlined, FilePdfOutlined, DatabaseOutlined, BarChartOutlined, SettingOutlined, QuestionCircleOutlined, LogoutOutlined } from '@ant-design/icons';
import { useUser, useClerk } from '@clerk/clerk-react';
import { useUserData } from '../contexts/UserDataContext';
import AdminWarningBanner from './AdminWarningBanner';
import FreeTrialBanner from './FreeTrialBanner';
import KeyboardShortcutsPage from './KeyboardShortcutsPage';
import '../styles/app-redesign.css';

const AppLayout = React.memo(({ activeTab, onTabChange, children }) => {
  const { user } = useUser();
  const { signOut } = useClerk();
  const { userProfile } = useUserData();
  const [showShortcutsPage, setShowShortcutsPage] = useState(false);
  
  // Memoize navItems to prevent recreation on every render
  const navItems = useMemo(() => [
    { key: 'dashboard', icon: <HomeOutlined />, label: 'Dashboard' },
    { key: 'analytics', icon: <BarChartOutlined />, label: 'Analytics' },
    { key: 'projects', icon: <ProjectOutlined />, label: 'Projects' },
    { key: 'documents', icon: <FilePdfOutlined />, label: 'Documents' },
    { key: 'takeoffs', icon: <DatabaseOutlined />, label: 'Takeoffs' },
  ], []);

  // Memoize page title calculation
  const pageTitle = useMemo(() => {
    const titles = {
      'dashboard': 'Dashboard',
      'analytics': 'Analytics',
      'projects': 'Projects',
      'documents': 'Documents',
      'takeoffs': 'Takeoff Items',
      'settings': 'Settings'
    };
    return titles[activeTab] || 'Dashboard';
  }, [activeTab]);

  const handleSignOut = () => {
    signOut(() => window.location.href = '/');
  };

  const handleShortcutsClick = () => {
    setShowShortcutsPage(true);
  };

  const handleBackFromShortcuts = () => {
    setShowShortcutsPage(false);
  };

  const handleTabChange = (tab) => {
    setShowShortcutsPage(false);
    onTabChange(tab);
  };

  const hasAdminBanner = userProfile?.app_role === 'admin';

  return (
    <div className={`app-redesign ${hasAdminBanner ? 'has-banner' : ''}`}>
      <AdminWarningBanner />
      <FreeTrialBanner />
      
      {/* Industrial Sidebar Navigation */}
      <aside className="app-sidebar-redesign">
        <div className="sidebar-grid-overlay"></div>
        
        <div className="sidebar-header-redesign">
          <div className="sidebar-brand-redesign">
            <div className="sidebar-logo-redesign">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                <rect x="4" y="4" width="24" height="24" stroke="currentColor" strokeWidth="2.5"/>
                <path d="M4 14h24M14 4v24" stroke="currentColor" strokeWidth="2.5"/>
                <circle cx="16" cy="16" r="3" fill="currentColor"/>
              </svg>
            </div>
            <div className="sidebar-brand-text">
              <span className="sidebar-brand-name">CIVILY</span>
              <span className="sidebar-brand-tagline">ESTIMATION ENGINE</span>
            </div>
          </div>

          {user && (
            <div className="sidebar-user-info-redesign">
              <img
                src={user.imageUrl}
                alt={user.fullName || user.primaryEmailAddress?.emailAddress}
                className="app-user-avatar"
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="sidebar-user-name">{user.fullName || user.firstName || 'User'}</div>
                <div className="sidebar-user-email">{user.primaryEmailAddress?.emailAddress}</div>
              </div>
            </div>
          )}
        </div>
        
        <nav className="sidebar-nav-redesign">
          <div className="sidebar-nav-section">
            <div className="sidebar-nav-label">Navigation</div>
            {navItems.map(item => (
              <button
                key={item.key}
                className={`sidebar-nav-item-redesign ${!showShortcutsPage && activeTab === item.key ? 'active' : ''}`}
                onClick={() => handleTabChange(item.key)}
              >
                <span className="sidebar-nav-icon">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </div>

          <div className="sidebar-nav-section">
            <div className="sidebar-nav-label">System</div>
            <button
              className={`sidebar-nav-item-redesign ${!showShortcutsPage && activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => handleTabChange('settings')}
            >
              <span className="sidebar-nav-icon"><SettingOutlined /></span>
              <span>Settings</span>
            </button>
            <button
              className={`sidebar-nav-item-redesign ${showShortcutsPage ? 'active' : ''}`}
              onClick={handleShortcutsClick}
            >
              <span className="sidebar-nav-icon"><QuestionCircleOutlined /></span>
              <span>Help</span>
            </button>
          </div>
        </nav>
        
        <div className="sidebar-footer-redesign">
          <button className="sidebar-signout-btn" onClick={handleSignOut}>
            <LogoutOutlined />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>
      
      <main className="app-main-redesign">
        <header className="app-header">
          <h1 className="app-header-title">{showShortcutsPage ? 'Keyboard Shortcuts' : pageTitle}</h1>
          <div className="app-header-actions">
            {!showShortcutsPage && (
              <button 
                className="app-icon-btn"
                onClick={handleShortcutsClick}
                title="Keyboard Shortcuts"
              >
                <QuestionCircleOutlined />
              </button>
            )}
            <button 
              className="app-icon-btn"
              onClick={() => handleTabChange('settings')}
              title="Settings"
            >
              <SettingOutlined />
            </button>
          </div>
        </header>
        
        {showShortcutsPage ? (
          <KeyboardShortcutsPage onBack={handleBackFromShortcuts} onNavigate={handleTabChange} />
        ) : (
          children
        )}
      </main>
    </div>
  );
});

AppLayout.displayName = 'AppLayout';

export default AppLayout;
